import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SLIDES = [
  {
    id: 'el-montoncito',
    badge: 'Juego Exclusivo MatiMarket',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    title: '¡JUEGA EL MONTONCITO!',
    titleHighlight: 'GANA MATICOINS',
    subtitle: 'Elige tu montón, apuesta con tus puntos y multiplica tus opciones de ganar grandes premios.',
    ctaText: '¡JUGAR AHORA!',
    ctaLink: '/juegos/el-montoncito',
    secondaryCtaText: 'Cómo Jugar',
    secondaryCtaLink: '/como-participar',
    isGame: true,
    bgGradient: 'from-black via-[#0d161a] to-[#0a1f18]',
    accentGlow: 'rgba(52, 211, 153, 0.25)',
    image: '/img/game/elmontoncito/reverso.png',
  },
  {
    id: 'proyector',
    badge: 'Tecnología Destacada',
    badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    title: 'CINE EN TU HOGAR',
    titleHighlight: 'PROYECTOR LED 2000 LM',
    subtitle: 'Transmite tus películas y series favoritas con resolución full y conectividad WiFi avanzada.',
    ctaText: 'Comprar o Participar',
    ctaLink: '/productos/detalle-producto/100001/comprar',
    bgGradient: 'from-black via-[#0d131f] to-[#0a1226]',
    accentGlow: 'rgba(59, 130, 246, 0.25)',
    image: '/fondos/fondo2.webp',
  },
  {
    id: 'cerradura',
    badge: 'Seguridad Inteligente',
    badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
    title: 'PROTEGE LO QUE MÁS QUIERES',
    titleHighlight: 'CERRADURA TOUCH WIFI',
    subtitle: 'Acceso por huella dactilar, clave y control remoto desde tu smartphone.',
    ctaText: 'Ver Detalles',
    ctaLink: '/productos/detalle-producto/100002/comprar',
    bgGradient: 'from-black via-[#170e24] to-[#12081f]',
    accentGlow: 'rgba(168, 85, 247, 0.25)',
    image: '/fondos/fondo3.webp',
  },
];

function ProductSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const handlePrevClick = () => {
    setCurrentIndex((prev) => (prev === 0 ? SLIDES.length - 1 : prev - 1));
  };

  const handleNextClick = () => {
    setCurrentIndex((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      handleNextClick();
    }, 6500);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused]);

  return (
    <div
      className="relative w-full h-[88vh] min-h-[580px] max-h-[820px] overflow-hidden bg-black select-none"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Slides */}
      {SLIDES.map((slide, index) => {
        const isActive = index === currentIndex;
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out bg-gradient-to-r ${slide.bgGradient} ${
              isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            {/* Ambient circular glow */}
            <div
              className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] rounded-full blur-[130px] pointer-events-none"
              style={{ background: slide.accentGlow }}
            ></div>

            {/* Pattern overlay */}
            <div className="absolute inset-0 bg-[radial-gradient(#ffffff0a_1px,transparent_1px)] [background-size:24px_24px] opacity-40"></div>

            <div className="relative z-20 max-w-7xl mx-auto h-full px-6 sm:px-12 flex flex-col justify-center">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-10">
                {/* Text Content */}
                <div className="lg:col-span-7 text-left space-y-5 animate-fadeIn">
                  {/* Badge */}
                  <div
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider ${slide.badgeColor}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-current animate-ping"></span>
                    <span>{slide.badge}</span>
                  </div>

                  {/* Title */}
                  <h2 className="text-4xl sm:text-6xl font-black text-white leading-tight tracking-tight">
                    {slide.title} <br />
                    <span className="text-gold-gradient drop-shadow-md">
                      {slide.titleHighlight}
                    </span>
                  </h2>

                  {/* Subtitle */}
                  <p className="text-base sm:text-lg text-gray-300 max-w-xl font-normal leading-relaxed">
                    {slide.subtitle}
                  </p>

                  {/* Call to Actions */}
                  <div className="flex flex-wrap items-center gap-4 pt-3">
                    <Link
                      to={slide.ctaLink}
                      className="px-8 py-4 rounded-xl font-black text-sm uppercase tracking-wider text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 shadow-xl shadow-gold/30 hover:shadow-gold/50 hover:scale-105 transition duration-200 flex items-center gap-2"
                    >
                      {slide.isGame ? (
                        <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      )}
                      <span>{slide.ctaText}</span>
                    </Link>

                    {slide.secondaryCtaLink && (
                      <Link
                        to={slide.secondaryCtaLink}
                        className="px-6 py-4 rounded-xl font-semibold text-sm text-gray-300 border border-gray-700 hover:border-gold/60 hover:text-white transition duration-200 backdrop-blur-md"
                      >
                        {slide.secondaryCtaText}
                      </Link>
                    )}
                  </div>
                </div>

                {/* Visual / Graphic element */}
                <div className="lg:col-span-5 hidden lg:flex items-center justify-center">
                  <div className="relative group">
                    <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-gold/40 via-amber-500/20 to-emerald-500/30 blur-xl opacity-60 group-hover:opacity-100 transition duration-500"></div>
                    
                    {slide.isGame ? (
                      /* Special Interactive Card for El Montoncito */
                      <Link
                        to="/juegos/el-montoncito"
                        className="relative block w-72 h-96 rounded-2xl glass-card border border-gold/40 p-6 shadow-2xl overflow-hidden hover:scale-105 transition duration-300 text-center"
                      >
                        <div className="absolute top-3 right-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                        </div>
                        <div className="text-xs uppercase tracking-widest text-gold font-bold mb-3">
                          Juego en Vivo
                        </div>
                        <div className="w-36 h-52 mx-auto my-2 rounded-xl bg-slate-900 border-2 border-gold/60 shadow-lg flex items-center justify-center overflow-hidden">
                          <img
                            src="/img/game/elmontoncito/reverso.png"
                            alt="El Montoncito Carta"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="mt-3 text-white font-bold text-base">
                          Mesa Activa
                        </div>
                        <div className="text-xs text-emerald-400 font-semibold mt-1">
                          ¡Haz clic para entrar a apostar!
                        </div>
                      </Link>
                    ) : (
                      /* Product/Store Card */
                      <Link
                        to={slide.ctaLink}
                        className="relative block w-80 h-96 rounded-2xl glass-card border border-gold/30 p-6 shadow-2xl overflow-hidden hover:scale-105 transition duration-300"
                      >
                        <div className="w-full h-56 rounded-xl overflow-hidden bg-slate-900/60 mb-4 flex items-center justify-center">
                          <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-contain p-2"
                          />
                        </div>
                        <div className="text-gold text-xs font-bold uppercase tracking-wider">
                          Destacado Oficial
                        </div>
                        <div className="text-white font-bold text-lg mt-1 line-clamp-1">
                          {slide.titleHighlight}
                        </div>
                        <div className="text-gray-400 text-xs mt-1">
                          Garantía y entrega garantizada
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Navigation Arrows */}
      <button
        onClick={handlePrevClick}
        aria-label="Anterior slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full glass-card border border-gray-700/80 hover:border-gold/70 text-white hover:text-gold flex items-center justify-center transition shadow-lg backdrop-blur-md"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={handleNextClick}
        aria-label="Siguiente slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full glass-card border border-gray-700/80 hover:border-gold/70 text-white hover:text-gold flex items-center justify-center transition shadow-lg backdrop-blur-md"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5">
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            aria-label={`Ir al slide ${idx + 1}`}
            className={`transition-all duration-300 rounded-full ${
              idx === currentIndex
                ? 'w-8 h-2.5 bg-gradient-to-r from-amber-400 to-yellow-400 shadow-md shadow-gold/50'
                : 'w-2.5 h-2.5 bg-gray-600 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default ProductSlider;
